import type { ParsePromptCsvResult, CsvPromptRow } from '../types';

const isLineBreak = (char: string): boolean => char === '\n' || char === '\r';

const trimBom = (input: string): string => {
  if (input.length && input.charCodeAt(0) === 0xfeff) {
    return input.slice(1);
  }
  return input;
};

const parseCsvMatrix = (content: string): string[][] => {
  const rows: string[][] = [];
  let currentField = '';
  let currentRow: string[] = [];
  let inQuotes = false;
  let prevCharWasCR = false;

  const pushField = () => {
    currentRow.push(currentField);
    currentField = '';
  };

  const pushRow = () => {
    if (currentRow.length || rows.length === 0) {
      rows.push(currentRow);
    }
    currentRow = [];
  };

  const input = trimBom(content);

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    if (inQuotes) {
      if (char === '"') {
        const nextChar = input[i + 1];
        if (nextChar === '"') {
          currentField += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        currentField += char;
      }
      prevCharWasCR = false;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      prevCharWasCR = false;
      continue;
    }

    if (char === ',') {
      pushField();
      prevCharWasCR = false;
      continue;
    }

    if (isLineBreak(char)) {
      if (char === '\r') {
        prevCharWasCR = true;
      } else if (char === '\n' && prevCharWasCR) {
        prevCharWasCR = false;
      }
      pushField();
      pushRow();
      currentField = '';
      continue;
    }

    if (prevCharWasCR) {
      pushField();
      pushRow();
      currentField = '';
      prevCharWasCR = false;
    }

    currentField += char;
  }

  pushField();
  if (currentRow.length || currentField.length) {
    pushRow();
  }

  return rows;
};

const detectPromptColumnIndex = (header: string[]): number => {
  const normalizedHeader = header.map((cell) => cell.trim().toLowerCase());
  const promptIndex = normalizedHeader.findIndex((cell) => cell === 'prompt' || cell === 'prompts');
  if (promptIndex >= 0) return promptIndex;
  return 0;
};

const sanitizeRows = (matrix: string[][]): string[][] => {
  const result: string[][] = [];
  for (const row of matrix) {
    if (!row.length) continue;
    if (row.length === 1 && !row[0].trim()) {
      continue;
    }
    result.push(row);
  }
  return result;
};

export const parsePromptCsvContent = (content: string): ParsePromptCsvResult => {
  const warnings: string[] = [];
  const errors: string[] = [];
  const matrix = sanitizeRows(parseCsvMatrix(content));
  if (!matrix.length) {
    return { rows: [], warnings: [], errors: [] };
  }

  let promptColumnIndex = 0;
  let dataStartRow = 0;

  if (matrix.length > 0) {
    const [firstRow] = matrix;
    const normalized = firstRow.map((cell) => cell.trim().toLowerCase());
    const containsPromptHeader = normalized.some((cell) => cell === 'prompt' || cell === 'prompts');
    if (containsPromptHeader) {
      promptColumnIndex = detectPromptColumnIndex(firstRow);
      dataStartRow = 1;
    }
  }

  const rows: CsvPromptRow[] = [];
  let dataIndex = 0;
  for (let rowIndex = dataStartRow; rowIndex < matrix.length; rowIndex += 1) {
    const row = matrix[rowIndex];
    const prompt = (row[promptColumnIndex] ?? '').trim();
    const lineNumber = rowIndex + 1;
    if (!prompt) {
      warnings.push(`第 ${lineNumber} 行缺少 Prompt，已跳过。`);
      continue;
    }
    dataIndex += 1;
    rows.push({
      prompt,
      rawFields: [...row],
      lineNumber,
      dataIndex,
    });
  }

  if (!rows.length && !warnings.length) {
    warnings.push('未在 CSV 中检测到有效的 Prompt。');
  }

  return { rows, warnings, errors };
};
